import React from 'react';
import { Check, Clock } from 'lucide-react';
import { IntegrationLogo } from '../IntegrationLogo';
import { EXPORT_BODY, EXPORT_LABEL, EXPORT_META, EXPORT_TITLE, ExportPanel, PanelHeader } from './exportUi';

/**
 * TIME RETURNED — the UI, without the marketing card around it.
 *
 * WHY THIS REPLACES "KILL THE STATUS MEETING" ON THE EXPORT PAGE. That card is
 * a headline, a paragraph, and three meetings with strikes drawn through them.
 * On the home page the headline is doing the work. As a downloadable asset the
 * headline is the problem: you cannot drop a marketing claim into a product
 * video without it reading as an ad, and the three struck rows on their own do
 * not say what came back.
 *
 * So this is the same fact stated as product UI. The recurring meetings Giga
 * replaced, what it posts instead, and — the number anyone making a video
 * actually wants on screen — the hours returned, per person, per week, with
 * the arithmetic visible above it rather than asserted.
 *
 * No headline, no persuasion, no container copy. A panel a viewer would
 * believe is a screen from the product, because structurally it is one.
 */

const REPLACED = [{
  name: 'Monday pipeline review',
  cadence: 'Weekly · 45 min',
  attendees: 9,
  posts: 'Recap in #revenue',
  tool: 'slack' as const,
  toolName: 'Slack'
}, {
  name: 'Ops check-in',
  cadence: 'Weekly · 30 min',
  attendees: 6,
  posts: 'Recap in #ops',
  tool: 'slack' as const,
  toolName: 'Slack'
}, {
  name: 'Support triage stand-up',
  cadence: 'Daily · 15 min',
  attendees: 4,
  posts: 'Digest in Teams',
  tool: 'microsoftteams' as const,
  toolName: 'Microsoft Teams'
}];

/** The three figures worth putting on screen. Hours first — it is the one a
 * viewer reads without being told how to. */
const TOTALS = [{
  label: 'Per person, weekly',
  value: '2h 15m'
}, {
  label: 'Across the team',
  value: '19h'
}, {
  label: 'Recaps posted',
  value: '312'
}];
export function TimeSavedPanel() {
  return <ExportPanel data-id="element-3355" data-mp="36bd430t9zsma">
      <PanelHeader label="Time returned" title="Meetings Giga now reports on" meta="3 recurring meetings replaced · last 30 days" aside={<span className="inline-flex items-center gap-1.5 rounded-full bg-[#F1EAE0] px-3 py-1.5 text-[12px] font-bold text-[#6B5D52]" data-id="element-3357" data-mp="31c0fobmradhl">
          <Clock size={12} strokeWidth={2.6} className="text-[#EA580C]" data-id="element-3358" data-mp="3bapzwb24ka10" />
          19h back this week
        </span>} data-id="element-3356" data-mp="36atuc7ds4ajc" />

      <ul className="px-6" data-id="element-3359" data-mp="2nklwa3oxsaa0">
        {REPLACED.map(meeting => <li key={meeting.name} className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[#F1EAE0] py-3.5 last:border-0" data-id="element-3360" data-mp="2nleyqsgv4dmc">
            <span aria-hidden="true" className="flex size-8 flex-none items-center justify-center rounded-xl bg-[#F1EAE0]" data-id="element-3361" data-mp="2xaoqg77snu9x">
              <Check size={14} strokeWidth={3} className="text-[#16A34A]" data-id="element-3362" data-mp="2sj6yg8nr1413" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-3363" data-mp="24tuj0r1tvzqc">
              {/* The meeting name carries the strike, so the row states the
               removal without needing a word for it. The line is present
               rather than drawn — an asset is a still, and an animation that
               has to run first is an asset that exports wrong. */}
              <span className={`${EXPORT_TITLE} block truncate`} data-id="element-3364" data-mp="k6bistmzr4lk">
                <span className="line-through decoration-[#EA580C] decoration-[2px]" data-id="element-3365" data-mp="5gnhhset85xu">{meeting.name}</span>
              </span>
              <span className={`${EXPORT_META} mt-0.5 block truncate`} data-id="element-3366" data-mp="aep3al3mejd0">
                {meeting.cadence} · {meeting.attendees} people
              </span>
            </span>
            {/* What replaced it. A removal with nothing in its place is a
             worse product, so the substitute is on the same row. */}
            <span className="flex flex-none items-center gap-2 rounded-full border border-[#EFE7DA] bg-white py-1 pl-1 pr-3" data-id="element-3367" data-mp="p5o8rn95srsd">
              <IntegrationLogo slug={meeting.tool} name={meeting.toolName} className="size-[18px]" rounded="rounded-full" data-id="element-3368" data-mp="39t13bggmtr4u" />
              <span className="text-[12px] font-semibold text-[#2B211A]" data-id="element-3369" data-mp="3dvxg2qjhqt0v">{meeting.posts}</span>
            </span>
          </li>)}
      </ul>

      {/* The payoff, as a row of gauges. Plain arithmetic from the rows above
       rather than a claim to be taken on faith. */}
      <dl className="mt-1 grid grid-cols-3 border-t border-[#EFE7DA] bg-[#FAF8F4]" data-id="element-3370" data-mp="3kbxuzs626ax7">
        {TOTALS.map((total, i) => <div key={total.label} className={`px-6 py-4 ${i > 0 ? 'border-l border-[#EFE7DA]' : ''}`} data-id="element-3371" data-mp="3fe5u48nfregv" data-mpi={i}>
            <dt className={EXPORT_LABEL} data-id="element-3372" data-mp="gw0gj70v6gt2">{total.label}</dt>
            <dd className="font-title mt-1.5 text-[26px] font-bold leading-none tracking-[-0.02em] text-[#2B211A]" data-id="element-3373" data-mp="gte9r9lss5bw">
              {total.value}
            </dd>
          </div>)}
      </dl>

      {/* One line of context, in product voice — what the number is measured
       against, not why it matters. */}
      <p className={`${EXPORT_BODY} border-t border-[#EFE7DA] px-6 py-3.5`} data-id="element-3374" data-mp="2rntpp7refzv1">
        Measured against the calendar time these meetings held before Giga took
        over reporting on them.
      </p>
    </ExportPanel>;
}